import { z } from 'zod';

import {
  CursorPaginationMetaSchema,
  ListSortOrderSchema,
  PaginationSchema,
} from '../../pagination.js';

export const StockMovementTypeSchema = z.enum([
  'ENTRY',
  'SALE_EXIT',
  'SERVICE_ORDER_EXIT',
  'TRANSFER_OUT',
  'TRANSFER_IN',
  'ADJUSTMENT_IN',
  'ADJUSTMENT_OUT',
  'RETURN_IN',
  'RETURN_OUT',
  'LOSS_OUT',
  'RECALL_BLOCK',
  'RECALL_UNBLOCK',
]);

export type StockMovementType = z.infer<typeof StockMovementTypeSchema>;

export const StockMovementGroupFilterSchema = z.enum([
  'all',
  'entries',
  'exits',
  'service_order',
  'transfers',
  'adjustments',
]);

export type StockMovementGroupFilter = z.infer<typeof StockMovementGroupFilterSchema>;

export const ListStockMovementsParamsSchema = z.object({
  placeId: z.uuid(),
});

export const StockMovementListSortFieldSchema = z.enum([
  'occurredAt',
  'productName',
  'movementType',
  'quantity',
  'lotCode',
  'storage',
  'source',
]);
export type StockMovementListSortField = z.infer<typeof StockMovementListSortFieldSchema>;

export const ListStockMovementsQuerySchema = PaginationSchema.extend({
  q: z.preprocess(
    (val) => (typeof val === 'string' && val.trim() === '' ? undefined : val),
    z.string().trim().min(1).max(100).optional(),
  ),
  placeProductId: z.uuid().optional(),
  movementType: StockMovementTypeSchema.optional(),
  movementGroup: StockMovementGroupFilterSchema.optional().default('all'),
  sort: StockMovementListSortFieldSchema.optional(),
  order: ListSortOrderSchema.optional(),
});

export type ListStockMovementsQuery = z.infer<typeof ListStockMovementsQuerySchema>;

export const StockMovementListItemSchema = z.object({
  id: z.uuid(),
  placeId: z.uuid(),
  placeProductId: z.uuid(),
  tenantProductId: z.uuid(),
  productName: z.string(),
  productSku: z.string().nullable(),
  movementType: StockMovementTypeSchema,
  quantity: z.string(),
  lotCode: z.string().nullable(),
  placeStorageName: z.string().nullable(),
  ownershipType: z.enum(['OWNED', 'THIRD_PARTY', 'VIRTUAL']).nullable(),
  sourceType: z.string(),
  sourceId: z.string().nullable(),
  sourceReferenceLabel: z.string().nullable(),
  occurredAt: z.iso.datetime(),
  reversedByMovementId: z.uuid().nullable(),
  adjustmentReason: z.string().nullable(),
  unitCostCents: z.string().nullable(),
  currency: z.string().nullable(),
  isAcceleratedReconciliation: z.boolean(),
  isReversalMovement: z.boolean(),
  wasReversed: z.boolean(),
});

export type StockMovementListItem = z.infer<typeof StockMovementListItemSchema>;

export const ListStockMovementsResponseSchema = z.object({
  items: z.array(StockMovementListItemSchema),
  pagination: CursorPaginationMetaSchema,
});

export type ListStockMovementsResponse = z.infer<typeof ListStockMovementsResponseSchema>;

export const StockMovementParamsSchema = z.object({
  movementId: z.uuid(),
});

export const StockMovementDetailSchema = StockMovementListItemSchema.extend({
  placeProductLotId: z.uuid(),
  placeStorageId: z.uuid(),
  totalCostCents: z.string().nullable(),
  notes: z.string().nullable(),
});

export type StockMovementDetail = z.infer<typeof StockMovementDetailSchema>;
